import { useCallback, useState } from "react";
import QuoteDetailsHeader from "./QuoteDetailsHeader";
import QuoteWorkspaceTabs from "./QuoteWorkspaceTabs";

import EstimateWorkspace from "../features/quote/workspaces/EstimateWorkspace";
import ScopeWorkspace from "../features/quote/workspaces/ScopeWorkspace";
import QuoteTextWorkspace from "../features/quote/workspaces/QuoteTextWorkspace";
import OverridesWorkspace from "../features/quote/workspaces/OverridesWorkspace";
import HistoryWorkspace from "../features/quote/workspaces/HistoryWorkspace";
import TimeWorkspace from "../features/quote/workspaces/TimeWorkspace";

import { User, Mail, Phone } from "lucide-react";

function isExcluded(row = {}) {
  return (
    row.excluded === true ||
    row?.flags?.excluded === true ||
    row?.meta?.excluded === true
  );
}

function CustomerStrip({ quote }) {
  const customer = quote?.customer;
  const customerName =
    quote?.customer_name ||
    customer?.name ||
    customer?.full_name ||
    customer?.company_name ||
    customer?.email ||
    "";

  if (!customerName) return null;

  return (
    <div className="border-b border-slate-200 bg-white px-4 py-2">
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
          <User size={14} />
          {customerName}
        </span>

        {customer?.email && (
          <span className="inline-flex items-center gap-1.5">
            <Mail size={13} />
            {customer.email}
          </span>
        )}

        {customer?.phone && (
          <span className="inline-flex items-center gap-1.5">
            <Phone size={13} />
            {customer.phone}
          </span>
        )}
      </div>
    </div>
  );
}

export default function QuoteDetailsPage(props) {
  const {
    quote,
    setCustomer,
    onCustomerChange,
    estimate,
    parsedData,
    setParsedData,
    errors,
    windows,
    setWindows,
    doors,
    setDoors,
    roofs,
    setRoofs,
    isBusy,
    onRebuild,
    onOpenBoom,
    onSave,
    onRegenerateQuoteText,
    onUpdateEstimateRow,
    onUpdateTimeRows,
    onChangeMarginPercent,
    onUndo,
    canUndo = false,
    lastSavedAt,
    versions,
    onRestoreVersion,
    selectedCurrency,
    formatDateTime,
    quoteValidation,
  } = props;

  const [workspaceTab, setWorkspaceTab] = useState("estimate");

  const handleToggleRowIncluded = useCallback(
    (row) => {
      if (!row || !onUpdateEstimateRow) return;

      const nextExcluded = !isExcluded(row);
      const rowKind = row?.meta?.rowKind || row?.type || "material";

      onUpdateEstimateRow(row.id, rowKind, {
        excluded: nextExcluded,
        inactive: nextExcluded,
        active: !nextExcluded,
        flags: {
          ...(row.flags || {}),
          excluded: nextExcluded,
          inactive: nextExcluded,
          active: !nextExcluded,
        },
        meta: {
          ...(row.meta || {}),
          excluded: nextExcluded,
          inactive: nextExcluded,
          active: !nextExcluded,
        },
      });
    },
    [onUpdateEstimateRow]
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-100 text-slate-900">
      <QuoteDetailsHeader
  quote={quote}
  setCustomer={setCustomer}
  onCustomerChange={onCustomerChange}
  quoteValidation={quoteValidation}
  onSave={onSave}
  onUndo={onUndo}
  canUndo={canUndo}
  isBusy={isBusy}
  lastSavedAt={lastSavedAt}
/>

      <CustomerStrip quote={quote} />

      <QuoteWorkspaceTabs
        activeTab={workspaceTab}
        onChange={setWorkspaceTab}
        quoteValidation={quoteValidation}
      />

      <main className="min-h-0 flex-1 overflow-hidden bg-slate-100">
        {workspaceTab === "estimate" && (
          <EstimateWorkspace
            estimate={estimate}
            parsedData={parsedData}
            onRebuild={onRebuild}
            onOpenBoom={onOpenBoom}
            onSave={onSave}
            onUndo={onUndo}
            canUndo={canUndo}
            isBusy={isBusy}
            onToggleRowIncluded={handleToggleRowIncluded}
            onChangeMarginPercent={onChangeMarginPercent}
          />
        )}

        {workspaceTab === "scope" && (
          <ScopeWorkspace
            quote={quote}
            estimate={estimate}
            parsedData={parsedData}
            setParsedData={setParsedData}
            errors={errors}
            windows={windows}
            setWindows={setWindows}
            doors={doors}
            setDoors={setDoors}
            roofs={roofs}
            setRoofs={setRoofs}
            isBusy={isBusy}
            onChangeMarginPercent={onChangeMarginPercent}
          />
        )}

        {workspaceTab === "quote" && (
          <QuoteTextWorkspace
            quote={quote}
            quoteValidation={quoteValidation}
            onRegenerateQuoteText={onRegenerateQuoteText}
            isBusy={isBusy}
          />
        )}

        {workspaceTab === "overrides" && (
          <OverridesWorkspace
            estimate={estimate}
            quote={quote}
            onUpdateEstimateRow={onUpdateEstimateRow}
            onSave={onSave}
            onRebuild={onRebuild}
            onUndo={onUndo}
            canUndo={canUndo}
            isBusy={isBusy}
          />
        )}

        {workspaceTab === "time" && (
          <TimeWorkspace
            estimate={estimate}
            parsedData={parsedData}
            selectedCurrency={selectedCurrency}
            onUpdateTimeRows={onUpdateTimeRows}
          />
        )}

        {workspaceTab === "history" && (
          <HistoryWorkspace
            versions={versions}
            onRestoreVersion={onRestoreVersion}
            selectedCurrency={selectedCurrency}
            formatDateTime={formatDateTime}
          />
        )}
      </main>
    </div>
  );
}